import { showToastMessage } from "../toast_message.js";
import { handleFetchError } from "../fetch_utils.js";

const RECAPTCHA_ACTION = "identify_signature";
let recaptchaLoading = null;

function loadRecaptcha(siteKey) {
    if (!recaptchaLoading) {
        recaptchaLoading = new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
            script.onload = () => grecaptcha.enterprise.ready(resolve);
            script.onerror = () => {
                recaptchaLoading = null;
                reject(new Error("Couldn't load reCAPTCHA"));
            };
            document.head.appendChild(script);
        });
    }
    return recaptchaLoading;
}

async function getRecaptchaToken(siteKey) {
    await loadRecaptcha(siteKey);
    return grecaptcha.enterprise.execute(siteKey, { action: RECAPTCHA_ACTION });
}

// Errors the server marks with return_logging: false are expected (bad image,
// rate limit, etc.) and shown as warnings rather than logged.
export function handleSignatureError(err) {
    if (err.userFacing) {
        showToastMessage(err.message, "warning");
    } else {
        handleFetchError(err);
    }
}

// Matches the server's working size; anything bigger only slows the upload
const MAX_UPLOAD_DIMENSION = 1600;
const SKIP_RESIZE_BELOW_BYTES = 1_500_000;

// Scales large photos down to a JPEG before upload. Falls back to the original
// file if the browser can't decode it; the server validates either way.
async function shrinkImage(file) {
    if (!window.createImageBitmap) return file;
    try {
        const bitmap = await createImageBitmap(file);
        const scale = Math.min(1, MAX_UPLOAD_DIMENSION / Math.max(bitmap.width, bitmap.height));
        if (scale === 1 && file.size < SKIP_RESIZE_BELOW_BYTES) {
            bitmap.close();
            return file;
        }
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(bitmap.width * scale);
        canvas.height = Math.round(bitmap.height * scale);
        const context = canvas.getContext("2d");
        context.fillStyle = "#fff";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        bitmap.close();
        const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", 0.9));
        return blob ? new File([blob], "signature.jpg", { type: "image/jpeg" }) : file;
    } catch {
        return file;
    }
}

function signatureError(message, userFacing) {
    const err = new Error(message);
    err.userFacing = userFacing;
    return err;
}

const LOOKUP_TIMEOUT_MS = 3 * 60 * 1000;

// Posts the form with XMLHttpRequest rather than fetch so we get upload
// progress, and reads the server's newline-delimited JSON events as they arrive.
function postForEvents(url, formData, onUploadProgress, onEvent) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let consumed = 0;
        let outcome = null;

        function readEvents() {
            const text = xhr.responseText;
            let newline;
            while ((newline = text.indexOf("\n", consumed)) !== -1) {
                const line = text.slice(consumed, newline).trim();
                consumed = newline + 1;
                if (!line) continue;
                const event = JSON.parse(line);
                if (event.type === "result" || event.type === "error") {
                    outcome = event;
                } else {
                    onEvent(event);
                }
            }
        }

        xhr.upload.addEventListener("progress", event => {
            if (event.lengthComputable) onUploadProgress(event.loaded / event.total);
        });
        xhr.addEventListener("progress", () => {
            if (xhr.status === 200) readEvents();
        });
        xhr.addEventListener("load", () => {
            if (xhr.status !== 200) {
                // Validation, reCAPTCHA and rate-limit failures are plain JSON responses
                let data = {};
                try {
                    data = JSON.parse(xhr.responseText);
                } catch {}
                reject(signatureError(data.error || "Error identifying signature", data.return_logging === false));
                return;
            }
            try {
                readEvents();
            } catch {
                reject(signatureError("Error identifying signature", false));
                return;
            }
            if (outcome?.type === "result") {
                resolve(outcome);
            } else if (outcome?.type === "error") {
                reject(signatureError(outcome.error, outcome.return_logging === false));
            } else {
                reject(signatureError("The lookup was interrupted, please try again", true));
            }
        });
        xhr.addEventListener("error", () => {
            reject(signatureError("Couldn't reach PewPew.Cards, check your connection and try again", true));
        });
        xhr.addEventListener("timeout", () => {
            reject(signatureError("The lookup took too long, please try again", true));
        });

        xhr.open("POST", url);
        // Backstop in case the server never finishes; its own model calls time out well before this
        xhr.timeout = LOOKUP_TIMEOUT_MS;
        xhr.send(formData);
    });
}

// Runs a lookup, reporting progress through onProgress(stage, detail) where
// stage is "uploading", "reading", "comparing" or "matching".
export async function requestSignatureMatch(file, recaptchaSiteKey, onProgress = () => {}) {
    onProgress("uploading", {});
    const fd = new FormData();
    const upload = await shrinkImage(file);
    fd.append("image", upload, upload.name || "signature.jpg");
    if (recaptchaSiteKey) {
        fd.append("recaptcha_token", await getRecaptchaToken(recaptchaSiteKey));
    }
    return postForEvents("/api/signature/identify",
                         fd,
                         fraction => onProgress("uploading", { fraction }),
                         event => onProgress(event.stage, event));
}

const STAGE_LABELS = {
    uploading: "Uploading photo",
    reading: "Reading signature",
    comparing: "Comparing handwriting with card backs",
    matching: "Finding matches"
};

// A checklist of lookup steps: done steps get a check, the current one a spinner.
// "Comparing" only appears if the server actually runs that step.
export function createProgressChecklist(container) {
    const list = el("ul", "list-unstyled small mb-0");
    const rows = {};
    let current = null;

    function setState(stage, state) {
        const icon = rows[stage].querySelector(".signature-progress-icon");
        icon.replaceChildren();
        rows[stage].classList.toggle("text-muted", state === "pending");
        rows[stage].classList.toggle("fw-semibold", state === "active");
        if (state === "active") {
            const spinner = el("span", "spinner-border spinner-border-sm themed-spinner");
            spinner.setAttribute("aria-hidden", "true");
            icon.append(spinner);
        } else {
            icon.append(el("i", state === "done" ? "bi bi-check-circle-fill text-success" : "bi bi-circle"));
        }
    }

    function addRow(stage, before = null) {
        const row = el("li", "d-flex align-items-center gap-2 py-1");
        row.append(el("span", "signature-progress-icon"), el("span", "signature-progress-label", STAGE_LABELS[stage]));
        list.insertBefore(row, before);
        rows[stage] = row;
        setState(stage, "pending");
    }

    ["uploading", "reading", "matching"].forEach(stage => addRow(stage));
    container.replaceChildren(list);
    container.classList.remove("d-none");

    return {
        update(stage, detail = {}) {
            if (!rows[stage]) addRow(stage, rows.matching);
            const label = rows[stage].querySelector(".signature-progress-label");
            if (stage === "uploading" && detail.fraction !== undefined && detail.fraction < 1) {
                label.textContent = `${STAGE_LABELS.uploading} (${Math.round(detail.fraction * 100)}%)`;
            } else if (stage === "uploading") {
                label.textContent = STAGE_LABELS.uploading;
            }
            if (stage === "comparing" && detail.reference_count !== undefined) {
                if (detail.reference_count === 0) {
                    rows.comparing.remove();
                    delete rows.comparing;
                    return;
                }
                label.textContent = `Comparing handwriting with ${detail.reference_count} card backs`;
            }
            if (current !== stage) {
                // Steps only move forward, so everything before this one is done
                Object.keys(rows).forEach(name => {
                    if (rows[name].compareDocumentPosition(rows[stage]) & Node.DOCUMENT_POSITION_FOLLOWING) {
                        setState(name, "done");
                    }
                });
                setState(stage, "active");
                current = stage;
            }
        },
        hide() {
            container.classList.add("d-none");
            container.replaceChildren();
        }
    };
}

// Disables a button and shows a small spinner in it until the returned function is called.
export function showButtonBusy(button, busyText) {
    const original = Array.from(button.childNodes);
    const spinner = el("span", "spinner-border spinner-border-sm me-2");
    spinner.setAttribute("aria-hidden", "true");
    button.disabled = true;
    button.replaceChildren(spinner, document.createTextNode(busyText));
    return () => button.replaceChildren(...original);
}

// Runs a lookup with the progress checklist and busy controls, resolving to the
// result or null if it failed (errors are already shown to the user).
export async function runSignatureLookup({ file, recaptchaSiteKey, progressContainer, button, busyText, inputs = [] }) {
    const progress = createProgressChecklist(progressContainer);
    const restoreButton = showButtonBusy(button, busyText);
    inputs.forEach(input => input.disabled = true);
    try {
        return await requestSignatureMatch(file, recaptchaSiteKey, (stage, detail) => progress.update(stage, detail));
    } catch (err) {
        handleSignatureError(err);
        return null;
    } finally {
        progress.hide();
        restoreButton();
        inputs.forEach(input => input.disabled = false);
    }
}

function matchLabel(confidence) {
    if (confidence >= 0.75) {
        return { text: "Strong match", badge: "text-bg-success" };
    }
    if (confidence >= 0.55) {
        return { text: "Possible match", badge: "text-bg-warning" };
    }
    return { text: "Long shot", badge: "text-bg-secondary" };
}

const MATCH_TYPE_LABELS = {
    name: "Name",
    visual: "Handwriting"
};

function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
}

// Renders the match results into container. Each candidate gets either a
// "use this artist" button (onSelect) or a link to their sketches (galleryPath).
export function renderSignatureResults(container, data, { onSelect = null, galleryPath = null } = {}) {
    container.replaceChildren();

    if (data.readings && data.readings.length && data.legibility !== "illegible") {
        const reading = el("p", "small mb-2");
        reading.append(el("span", "text-muted", "Signature reads: "));
        reading.append(el("span", "fw-semibold", data.readings.map(r => `“${r}”`).join(" or ")));
        container.append(reading);
    }
    if (data.compared_visually) {
        container.append(el("p", "small text-muted mb-2",
            "This signature is hard to read, so we also compared its handwriting with card backs uploaded to PewPew.Cards."));
    }

    if (!data.candidates.length) {
        const message = data.signature_found
            ? "We couldn't match that signature to an artist on PewPew.Cards yet."
            : "We couldn't find a signature in that image. Try a closer, well-lit photo of the signature.";
        container.append(el("div", "alert alert-secondary small mb-0", message));
        return;
    }

    const list = el("div", "list-group");
    data.candidates.forEach(candidate => {
        const item = el("div", "list-group-item");

        const header = el("div", "d-flex justify-content-between align-items-center gap-2");
        const title = el("div");
        title.append(el("span", "fw-semibold me-2", candidate.name));
        const label = matchLabel(candidate.confidence);
        title.append(el("span", `badge ${label.badge} me-1`, label.text));
        (candidate.match_types || []).forEach(matchType => {
            title.append(el("span", "badge text-bg-light border me-1", MATCH_TYPE_LABELS[matchType] || matchType));
        });
        header.append(title);

        if (onSelect) {
            const useButton = el("button", "btn btn-sm btn-outline-primary", "Use");
            useButton.type = "button";
            useButton.addEventListener("click", () => onSelect(candidate));
            header.append(useButton);
        } else if (galleryPath && candidate.sketch_count > 0) {
            const link = el("a", "btn btn-sm btn-outline-primary", "View sketches");
            link.href = `${galleryPath}?artist=${encodeURIComponent(candidate.artist_id)}`;
            header.append(link);
        }
        item.append(header);

        if (candidate.reference_backs.length) {
            const thumbs = el("div", "d-flex flex-wrap gap-2 mt-2");
            candidate.reference_backs.forEach(url => {
                const img = el("img", "signature-ref-thumb border");
                img.src = url;
                img.loading = "lazy";
                img.alt = `Back of a sketch card by ${candidate.name}`;
                thumbs.append(img);
            });
            item.append(thumbs);
        } else {
            item.append(el("div", "text-muted small mt-1", "No card backs from this artist uploaded yet to compare against."));
        }
        list.append(item);
    });
    container.append(list);
}

// Wires up the standalone "Identify an Artist" modal, if it's on the page.
export function initSignatureIdentifyModal() {
    const modal = document.getElementById("identifySignatureModal");
    if (!modal) return;

    const fileInput = document.getElementById("signature-image");
    const identifyButton = document.getElementById("identify-signature-btn");
    const progressContainer = document.getElementById("identify-signature-progress");
    const results = document.getElementById("signature-results");
    const { recaptchaSiteKey, galleryPath } = modal.dataset;

    if (recaptchaSiteKey) {
        // Load ahead of the click so the token request is quick
        modal.addEventListener("show.bs.modal", () => {
            loadRecaptcha(recaptchaSiteKey).catch(() => {});
        }, { once: true });
    }

    fileInput.addEventListener("change", () => {
        identifyButton.disabled = !fileInput.files.length;
        results.replaceChildren();
    });

    identifyButton.addEventListener("click", async () => {
        if (!fileInput.files.length) return;
        results.replaceChildren();
        const data = await runSignatureLookup({
            file: fileInput.files[0],
            recaptchaSiteKey,
            progressContainer,
            button: identifyButton,
            busyText: "Identifying...",
            inputs: [fileInput]
        });
        if (data) renderSignatureResults(results, data, { galleryPath });
        identifyButton.disabled = !fileInput.files.length;
    });
}
